import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { apiUsuarios, type Papel, type SituacaoAcesso, type UsuarioDaCasa } from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { avisar } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { BlocoDeLancar, CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import '../estilos/formularios-curtos-2026.css';

/** O domínio da casa. Só e-mail daqui recebe convite — ver `rotas/usuarios.ts`. */
const DOMINIO = '@kairosasset.com.br';

const ROTULO: Record<SituacaoAcesso, string> = {
  ativo: 'Em uso',
  convite_pendente: 'Convite enviado',
  convite_vencido: 'Convite vencido',
  desativado: 'Desativado',
};

const ROTULO_PAPEL: Record<Papel, string> = {
  master: 'Administra o Hub',
  colaborador: 'Colaborador',
};

/**
 * Quem entra no Hub.
 *
 * Só o master chega aqui — a tela esconde a entrada e o servidor recusa a rota.
 *
 * **Não existe botão de apagar, e é de propósito.** Quem sai da KAIRÓS é
 * desativado: para de entrar na hora, e continua respondendo pelo que cadastrou
 * enquanto estava. Apagar levaria junto a resposta de "quem cadastrou este
 * cliente?", e essa trilha não se refaz depois.
 */
export function Usuarios({ eu, aoFechar }: { eu: string; aoFechar: () => void }) {
  const [lista, setLista] = useState<UsuarioDaCasa[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<Papel>('colaborador');
  const [salvando, setSalvando] = useState(false);
  const [aDesativar, setADesativar] = useState<UsuarioDaCasa | null>(null);
  const emVoo = useRef(false);

  usarDialogo(aoFechar);

  const carregar = useCallback(async () => {
    try {
      const r = await apiUsuarios.listar();
      setLista(r.usuarios);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui carregar os acessos.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function criar(evento: FormEvent) {
    evento.preventDefault();
    if (emVoo.current) return;

    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      const criado = await apiUsuarios.criar({ nome, email, papel });
      // Falha de envio não vira silêncio: sem isso o master acha que a pessoa
      // recebeu e fica esperando por quem nunca foi avisado.
      avisar(
        criado.aviso ?? `Convite enviado para ${criado.email}. O link vale 24 horas.`,
        criado.aviso ? 'ruim' : 'bom',
      );
      setNome('');
      setEmail('');
      setPapel('colaborador');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui criar o acesso.');
    } finally {
      emVoo.current = false;
      setSalvando(false);
    }
  }

  async function reenviar(u: UsuarioDaCasa) {
    try {
      const r = await apiUsuarios.reenviarConvite(u.id);
      avisar(r.aviso ?? `Convite reenviado para ${u.email}.`, r.aviso ? 'ruim' : 'bom');
      await carregar();
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui reenviar o convite.', 'ruim');
    }
  }

  async function alternarAtivo(u: UsuarioDaCasa, ativo: boolean) {
    try {
      await apiUsuarios.definirAtivo(u.id, ativo);
      avisar(ativo ? `${u.nome} voltou a ter acesso.` : `${u.nome} não entra mais no Hub.`);
      await carregar();
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui alterar o acesso.', 'ruim');
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (a janela em blocos): convidar em cima,
     os acessos embaixo, cada um numa linha com o que pode e em que pé está. */
  return (
    <>
      <JanelaEmBlocos
        rotulo="Quem entra no Hub"
        sobrelinha="Conta · acessos"
        titulo="Quem entra no Hub"
        sub="Quem sai da KAIRÓS é desativado, e não apagado: para de entrar na hora e continua respondendo pelo que cadastrou."
        largura="media"
        aoFechar={aoFechar}
        erro={erro}
        rodape={
          <>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" onClick={aoFechar}>
              Fechar
            </button>
          </>
        }
      >
        <BlocoDeLancar
          titulo="Convidar alguém"
          aoEnviar={(e) => void criar(e)}
          nota={
            <>
              <p>A pessoa escolhe a própria senha pelo link do e-mail, que vale 24 horas. Você nunca fica sabendo qual é.</p>
            </>
          }
          acoes={
            <button type="submit" className="imv-bt primario" disabled={salvando || !nome.trim() || !email.trim()}>
              <Icone nome="email" tamanho={14} /> {salvando ? 'Enviando convite…' : 'Criar acesso e enviar convite'}
            </button>
          }
        >
          <CampoDoPasso rotulo="Nome" obrigatorio col={3}>
            <input id="usuario-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} autoFocus />
          </CampoDoPasso>
          <CampoDoPasso rotulo="E-mail" obrigatorio col={3} nota={`Só e-mail ${DOMINIO}. Crie a caixa da pessoa no Workspace antes de convidar.`}>
            <input id="usuario-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`nome${DOMINIO}`} />
          </CampoDoPasso>
          <div className="imv-campo c6">
            <span className="imv-rotulo">O que essa pessoa pode</span>
            <div className="imv-seg" role="radiogroup" aria-label="O que essa pessoa pode">
              {(['colaborador', 'master'] as Papel[]).map((p) => (
                <button key={p} type="button" role="radio" aria-checked={papel === p} className={`imv-op neutro${papel === p ? ' on' : ''}`} onClick={() => setPapel(p)}>
                  <i />
                  {p === 'colaborador' ? 'Colaborador — vê e cadastra' : 'Administra o Hub — pode tudo'}
                </button>
              ))}
            </div>
            <small>
              O colaborador cadastra cliente e caso, move o caso de situação, conclui compromisso e registra reunião. Não edita nem apaga, e não enxerga
              valores.
            </small>
          </div>
        </BlocoDeLancar>

        <div className="cad-subtitulo">
          <h5>Acessos</h5>
        </div>
        {lista === null ? (
          <p className="imv-nada">Carregando…</p>
        ) : (
          <ul className="usu-lista">
            {lista.map((u) => (
              <li key={u.id} className={`usu-linha${u.ativo ? '' : ' usu-inativo'}`}>
                <span className="s-avatar" aria-hidden="true">
                  {u.nome.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x.charAt(0)).join('')}
                </span>
                <div className="usu-quem">
                  <p className="usu-nome">
                    {u.nome}
                    {u.id === eu && <span className="usu-voce"> (você)</span>}
                  </p>
                  <p className="usu-email">{u.email}</p>
                </div>
                <span className={`etiqueta${u.situacao === 'ativo' ? ' etiqueta-ativo' : ''}`}>
                  {ROTULO_PAPEL[u.papel]} · {ROTULO[u.situacao]}
                </span>
                <div className="usu-acoes">
                  {(u.situacao === 'convite_pendente' || u.situacao === 'convite_vencido') && (
                    <button type="button" className="imv-bt p" onClick={() => void reenviar(u)}>
                      Reenviar convite
                    </button>
                  )}
                  {u.id !== eu &&
                    (u.ativo ? (
                      <button type="button" className="imv-bt p fantasma" onClick={() => setADesativar(u)}>
                        Desativar
                      </button>
                    ) : (
                      <button type="button" className="imv-bt p" onClick={() => void alternarAtivo(u, true)}>
                        Reativar
                      </button>
                    ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </JanelaEmBlocos>

      {aDesativar && (
        <Confirmar
          titulo="Desativar o acesso"
          descricao={`${aDesativar.nome} deixa de entrar no Hub agora — se estiver com o sistema aberto, a sessão cai. O que essa pessoa cadastrou continua no lugar, com o nome dela. Dá para reativar depois.`}
          rotuloAcao="Desativar"
          aoConfirmar={() => void alternarAtivo(aDesativar, false)}
          aoFechar={() => setADesativar(null)}
        />
      )}
    </>
  );
}
